import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./AssignmentModalBody.scss";;
import { useDispatch, useSelector } from "react-redux";
import ActivityCard from "@/features/dashboard/components/ActivityCard";
import {
  updateCRUDAssignmentsQueue,
  updateAssignmentMutationLoadingState,
} from "@/store/slices";
import { ResourceType, MasteryStatusPacing } from "@/enums";
import { AppDispatch, Skill, RootState, AmiraResources, LessonDetails, AssignmentManifest } from "@/models";
import { ResourceTypeImages } from "@/constants/ActivityTypeImages";
import { AsyncLoadingState } from "@/features/dashboard/enums";
import { SkillCardHeader } from "./SkillCardHeader";
import CustomDropdown from './CustomDropdown';
import { ButtonThemed, UserBadge, UserBadgeStack } from "@/components/shared";
import AssignmentGrowth from "./AssignmentGrowth";
import { makeToast, ToastTypes } from "@/components/Global/Notifications";

import { handleCreateUpdateAssignments } from "@/utilities/WeeklyPlanSliceHelpers";
import { calculateTotalDuration } from "../utils";
import { getTimeAssignedByGrade, getSkillTimeBalance } from "@/utilities";
import { useFilteredResources } from "./hooks/useFilteredResources";
import { useAssignmentManagement } from "./hooks/useAssignmentManagement";
import { useAssignmentFilters } from "./hooks/useAssignmentFilters";
import { Placeholder } from "@/components/Global/Placeholder/Placeholder";

import addToPlanIcon from "@/images/icons/add-to-plan.svg";
import greenStars from "@/images/icons/green-star.svg";
import magnifyingGlassIcon from "@/images/icons/magnifying-glass-icon.svg";
import { skillResourceLicenses } from "@/constants/skillResourceLicenses";
import { useTranslation } from "react-i18next";
import purpleCloseIcon from "@/images/icons/purple-close-icon.svg";
import { MasteryBadge } from "@/features/planner/components/MasteryBadge";
import { Entity } from "@/services/assignment/assignmentTypesAPI";
import { transformId } from "@/utilities/transformId";
interface AssignmentModalConfig {
  resultsHeader: string;
  isTutoring?: boolean;
  searchHeader: string;
  students: any[];
  groupName: string;
  skillName: string;
  lesson: LessonDetails;
  skillId: string;
  skill: Skill;
  skillResources: AmiraResources[];
  onCloseHandler: () => void;
  standards: any;
  skillMastery: number | string;
  source: string;
}

// Add isNew prop to the ActivityCard component props

export default function AssignmentModalBody({
  isTutoring,
  searchHeader,
  students,
  groupName,
  skillName,
  lesson,
  skillId,
  skill,
  skillResources,
  onCloseHandler,
  standards,
  skillMastery,
  source
}: AssignmentModalConfig) {

  const dispatch = useDispatch<AppDispatch>();
  const { mutateAssignmentsLoading } = useSelector((state: RootState) => state.weeklyPlan);
  const { instruct, tutor } = useSelector((state: RootState) => state.userData.entitlements.subscriptions);
  const classGradeLevel = useSelector((state: RootState) => state.reports.roster.data.student);
  const skills = skillName.toLowerCase();
  const [isFiltering, setIsFiltering] = useState(false);

  // Initialize inputState directly here
  const [inputState, setInputState] = useState({
    assignmentsType: !isTutoring ? "all digital lessons" : "tutoring",
    providers: "all providers",
  });

  const {
    newAssignments,
    studentAssignmentsManifest,
    removingCurrentAssignments,
    addAssignmentsHandler,
    removeAssignmentHandler,
    removeCurrentAssignmentHandler,
    undoRemoveCurrentAssignment
  } = useAssignmentManagement(students, skillId);

  const {t} = useTranslation('assignment_modal');

  const currentlyAssignedStories: AmiraResources[] = useMemo(() => {
    const storiesMap = new Map<string, AmiraResources>();
    const nilUUIDInternal = "00000000-0000-0000-0000-000000000000";

    const skillTutorResource = skillResources?.find(res => res.resourceType === ResourceType.skillTutor);
    const multipleStoriesResources = skillResources
    .filter(
      resource =>
        resource.resourceType === ResourceType.EPS ||
        resource.resourceType === ResourceType.ERSS ||
        resource.resourceType === ResourceType.textSet
    )
    .map(resource => ({
      ...resource,
      storyIds: resource.storyIds?.map(id => transformId(id)),
    }));

    students.forEach(student => {
      
      student.assignment?.manifest?.forEach((manifestItem: any, index: number) => {
        
        // Only process manifest items for the current skill
        // This is needed to prevent removal of a skill tutor from one skill also removing the skill tutor from other skills
        if (!manifestItem.skills?.some((skill: any) => skill.skillId === skillId)) {
          return;
        }
        
        const storyId = transformId(manifestItem.storyId);
        const isSkillTutor = storyId === transformId(nilUUIDInternal);
        const resourceTypeFromManifest = isSkillTutor
          ? ResourceType.skillTutor
          : (manifestItem.activityType as ResourceType);

        let mapKey = `${storyId}-${resourceTypeFromManifest}`;
        let isMultipleStoriesResource = false;

        if (resourceTypeFromManifest === ResourceType.tutor) {
          // we need to covert back multiple stories into single resource
          // EPS, ERSS and textSet are multiple stories resources
          // if we have that resource, we need to use resourceUniqueId as mapKey
          for (const resource of multipleStoriesResources) {
            if (resource.storyIds?.includes(storyId)) {
              mapKey = `${transformId(resource.resourceUniqueId)}-${resourceTypeFromManifest}`;
              isMultipleStoriesResource = true;
              console.log(`  ✅ Found in multiple stories resource: ${resource.resourceUniqueId}`);
              break;
            }
          }
        }

        if (!storiesMap.has(mapKey)) {
          if (isSkillTutor && skillTutorResource) {
            storiesMap.set(mapKey, skillTutorResource);
          } else {
            let foundResourceInSkillResources: AmiraResources | undefined;
            if (isMultipleStoriesResource) {
              // if found multiple stories resource, we need to use multipleStoriesResources instead of skillResources
              foundResourceInSkillResources = multipleStoriesResources?.find(
                res => res.storyIds?.includes(storyId)
              );
            } else {
              foundResourceInSkillResources = skillResources?.find(
                res =>
                  transformId(res.resourceUniqueId) ===
                    (storyId ? transformId(storyId) : "") &&
                  res.resourceType === resourceTypeFromManifest
              );
            }
            if (foundResourceInSkillResources) {
              storiesMap.set(mapKey, foundResourceInSkillResources);
            }
          }
        }
      });
    });

    // Check if ALL students have individual assignments
    // Previously this was just checking if you were looking at a single student
    // We need this condition because you could be modifying for a group of students that all have student assignments already
    const allStudentsHaveIndividualAssignments = students.every(s => s.assignment?.entityType === Entity.STUDENT || !s.assignment);
    
    if (allStudentsHaveIndividualAssignments) {
      // If ALL students have individual assignments, use ONLY their individual resources
      students.forEach(student => {
        
        student.assignment?.resources?.forEach(
          (rId: string) => {
            const transformedRId = transformId(rId);
            
            // Find the resource group that matches BOTH the resource ID AND the current skill ID
            let resourceGroup = lesson.resources.find(r => 
              r.skillId === skillId && 
              r.resources.find(re => transformId(re.resourceUniqueId) === transformedRId)
            );
            
            let resource = resourceGroup?.resources.find(re => transformId(re.resourceUniqueId) === transformedRId);
            
            // Add teacher resources for the current skill
            if (resource && resourceGroup) {
                const storyId = resource.resourceUniqueId;
                const resourceType = resource.resourceType;
                const mapKey = `${storyId}-${resourceType}`;
                
                if (!storiesMap.has(mapKey)) {
                  storiesMap.set(mapKey, resource);
                }
            }
          }
        );
      });
    } else {
      // If any student does NOT have individual assignment, use class/district level resources
      lesson?.resources?.forEach(
        (r: { skillId: string; resources: AmiraResources[] }) => {
          if (r.skillId === skillId) {
            r.resources.forEach(resource => {
              const storyId = resource.resourceUniqueId;
              const resourceType = resource.resourceType;
              const mapKey = `${storyId}-${resourceType}`;
              if (!storiesMap.has(mapKey)) {
                storiesMap.set(mapKey, {...resource, sharedSkills: skillResources.find(r => r.resourceUniqueId === storyId)?.sharedSkills || []});
              }
            });
          }
        }
      );
    }

    const finalResult = Array.from(storiesMap.values());

    return finalResult;
  }, [students, skillResources, skillId, lesson.resources]);

  // First call useFilteredResources to get availableResources
  const { filteredResources, availableResources } = useFilteredResources(
    skillResources,
    students,
    lesson,
    instruct,
    tutor,
    inputState,
    "all digital lessons", // Use string literals for now
    "all providers",
    currentlyAssignedStories,
    newAssignments
  );

  // Then call useAssignmentFilters with availableResources
  const {
    providerOptions,
    assignmentTypeOptions,
    PLACEHOLDER_ASSIGNMENT_TEXT,
    PLACEHOLDER_PROVIDERS_TEXT
  } = useAssignmentFilters(availableResources, isTutoring);

  // Reset inputState when tutoring mode changes
  useEffect(() => {
    setInputState({
      assignmentsType: !isTutoring ? PLACEHOLDER_ASSIGNMENT_TEXT : "tutoring",
      providers: PLACEHOLDER_PROVIDERS_TEXT,
    });
  }, [isTutoring, PLACEHOLDER_ASSIGNMENT_TEXT, PLACEHOLDER_PROVIDERS_TEXT]);

  const batchAssignUnassignStoriesHandler = () => {
    const removedTeacherResources = removingCurrentAssignments
      .filter(r => r.resourceType === ResourceType.teacherResource)
      .map(r => r.resourceUniqueId);

    const currentTeacherResources = currentlyAssignedStories
      .filter(resource => resource.resourceType === ResourceType.teacherResource && !removedTeacherResources.includes(resource.resourceUniqueId))
      .map(r => r.resourceUniqueId);

    const newTeacherResources = newAssignments
      .filter(r => r.resourceType === ResourceType.teacherResource && !removedTeacherResources.includes(r.resourceUniqueId))
      .map(r => r.resourceUniqueId);

    const finalTeacherResources = Array.from(new Set([...currentTeacherResources, ...newTeacherResources]));

    // Always assign teacher resources when creating student assignments
    // The student assignments being created should include all relevant teacher resources
    // Because students could have had teacher resources added or teacher resources removed from district default
    const teacherResourcesPerStudent = studentAssignmentsManifest.map(s => {
      return { studentId: s.studentId, resources: finalTeacherResources };
    });

    // Use only the manifest for the current skill
    const originalStudentGroupManifestsForLogging = students.map(s => ({
      studentId: s.student.id,
      manifest:
        s.assignment?.manifest.filter((m: AssignmentManifest) =>
          m.skills?.some(sk => sk.skillId === skillId)
        ) ?? [],
    }));

    const result = handleCreateUpdateAssignments({
      lesson,
      userId: lesson.userId,
      classroomId: lesson.classroomId,
      newStudentManifests: studentAssignmentsManifest,
      teacherResources: teacherResourcesPerStudent,
      originalStudentGroupManifests: originalStudentGroupManifestsForLogging,
      removedTeacherResources: removedTeacherResources
    });

    if (result) {
      dispatch(updateCRUDAssignmentsQueue(result));
      dispatch(updateAssignmentMutationLoadingState(AsyncLoadingState.LOADING));
    } else {
      makeToast('Failed to prepare assignment updates.', ToastTypes.warning, true, 5000);
    }
  };

  const newAssignmentsCount = newAssignments.length;
  const removedAssignmentsCount = removingCurrentAssignments.length;

   useEffect(() => {
    if (mutateAssignmentsLoading === AsyncLoadingState.SUCCESS) {
      onCloseHandler();
      const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
      const createdMessage = pluralize(newAssignmentsCount, "assignment");
      const removedMessage = pluralize(removedAssignmentsCount, "assignment");
      makeToast(`${createdMessage} created, ${removedMessage} removed`, ToastTypes.success, true, 5000);
      dispatch(updateAssignmentMutationLoadingState(AsyncLoadingState.NONE));
    } else if (mutateAssignmentsLoading === AsyncLoadingState.ERROR) {
      makeToast('Error updating assignments', ToastTypes.warning, true, 5000);
      dispatch(updateAssignmentMutationLoadingState(AsyncLoadingState.NONE));
    }
  }, [mutateAssignmentsLoading, onCloseHandler, dispatch, newAssignmentsCount, removedAssignmentsCount]);


  const isActivityAdded = (resourceUniqueId: string) => {
      return newAssignments.some(resource => resource.resourceUniqueId === resourceUniqueId);
    };

  const getTimeAssigned = useCallback((resourceType: string, storiesCount: number) => {
    const time = getTimeAssignedByGrade(resourceType, classGradeLevel ?? [{grade: '0'}]);
    if (resourceType === ResourceType.EPS || resourceType === ResourceType.ERSS || resourceType === ResourceType.textSet) {
      return time * storiesCount;
    }
    return time;
  }, [classGradeLevel]);


  const totalDuration = useMemo(() => {
    return calculateTotalDuration(newAssignments, currentlyAssignedStories, removingCurrentAssignments, getTimeAssigned);
  }, [newAssignments, currentlyAssignedStories, removingCurrentAssignments, getTimeAssigned]);

  const {
    timeToGrowth,
    skillBookingStatus
  } = useMemo(() => getSkillTimeBalance(skillMastery, totalDuration, groupName as MasteryStatusPacing), [
    skillMastery,
    groupName,
    totalDuration
  ]);
  const handleFilterChange = (field: 'assignmentsType' | 'providers', value: string) => {
    setIsFiltering(true);
    setTimeout(() => {
      setInputState((prevState: { providers: string; assignmentsType: string }) => ({ ...prevState, [field]: value }));
      setIsFiltering(false);
    }, 1000);
  };


  const categorizeAssignments = (resources: AmiraResources[], newAssignments: AmiraResources[]) => {
    const newAssignmentIds = new Set(newAssignments.map(res => res.resourceUniqueId));

    const allResources = [...resources];
    newAssignments.forEach(newResource => {
      if (!resources.some(r => r.resourceUniqueId === newResource.resourceUniqueId)) {
        allResources.push(newResource);
      }
    });

    const teacherResources = allResources.filter(resource =>
      skillResourceLicenses.others.includes(resource.resourceType as ResourceType));

    const microlessons = allResources.filter(resource =>
      skillResourceLicenses.instruct.includes(resource.resourceType as ResourceType));

    const tutoring = allResources.filter(resource =>
      skillResourceLicenses.tutor.includes(resource.resourceType as ResourceType));

    return {
      teacherResources,
      microlessons,
      tutoring,
      isNewResource: (id: string) => newAssignmentIds.has(id)
    };
  };

  const {
    teacherResources: categorizedTeacherResources,
    microlessons: categorizedMicrolessons,
    tutoring: categorizedTutoring,
    isNewResource
  } = categorizeAssignments(
    currentlyAssignedStories.filter(resource =>
      !removingCurrentAssignments.some(item => item.resourceUniqueId === resource.resourceUniqueId)),
    newAssignments
  );

  return (
    <div className="assignment-modal-container">
    <div className="assignment-modal-container-content">

    <div className="assignment-modal-assigned-content d-flex flex-column align-items-center">
      <div className="assignment-modal-assigned-content-header-title"> {t('currently_assigned_for')}
        <div className="mastery-student-list-info-students d-flex align-items-center gap-1">
          {students.length > 1 ? (
              <UserBadgeStack
                students={students.map((s) => `${s.student.first_name} ${s.student.last_name}`)} />
            ) : (
              students.length > 0 && <span className="mastery-student-list-info-students-name d-flex align-items-center gap-2">
                <UserBadge name={`${students[0].student.first_name} ${students[0].student.last_name}`} />
                {`${students[0].student.first_name} ${students[0].student.last_name}`}
              </span>
          )}
          </div>

          <MasteryBadge status={groupName} />

            <button className="close-assignment-modal-button" onClick={onCloseHandler}><img src={purpleCloseIcon} alt="close icon" className="close-assignment-modal-button-icon" /></button>
        </div>
      <div className="assignment-modal-assigned-content-wrapper">
      <SkillCardHeader skillName={skillName} standards={standards} domain={`${skillId?.split('.')[0]}`} source={source}/>
      <AssignmentGrowth duration={totalDuration} skillBookingStatus={skillBookingStatus} timeToGrowth={timeToGrowth} />
      <div className="assignment-modal-assigned-content-header d-flex flex-column align-items-start" style={{ width: '37.5rem'}}>
              <div className="assignment-modal-assigned-content-items">
              <div className="assignment-modal-assigned-content-new-header-title">
                <span>Teacher Resources</span>
                <span style={{ marginLeft: 'auto' }}>{categorizedTeacherResources.length}</span>
              </div>
              {categorizedTeacherResources.map((resource: AmiraResources) => (
                <ActivityCard
                  key={`${resource.resourceUniqueId}-current-teacher`}
                  activityId={resource.resourceUniqueId}
                  title={resource.title}
                  subTitle={resource.resourceType}
                  iconSrc={ResourceTypeImages[resource.resourceType as keyof typeof ResourceTypeImages] || resource.resourceIcon || ""}
                  timeAssigned={getTimeAssigned(resource.resourceType, resource.storyIds?.length ?? 1)}
                  unassignFromPlan={isNewResource(resource.resourceUniqueId) ? () => removeAssignmentHandler(resource) : () => removeCurrentAssignmentHandler(resource)}
                  isRemoving={false}
                  isNew={isNewResource(resource.resourceUniqueId)}
                  url={resource.url}
                  sharedSkills={resource.sharedSkills}
                />
              ))}

              <div className="assignment-modal-assigned-content-new-header-title">
                <span>Microlessons</span>
                <span style={{ marginLeft: 'auto' }}>{categorizedMicrolessons.length}</span>
              </div>
              {categorizedMicrolessons.map((resource: AmiraResources) => (
                <ActivityCard
                  key={`${resource.resourceUniqueId}-current-microlesson`}
                  activityId={resource.resourceUniqueId}
                  title={resource.title}
                  subTitle={resource.resourceType}
                  iconSrc={ResourceTypeImages[resource.resourceType as keyof typeof ResourceTypeImages] || resource.resourceIcon || ""}
                  timeAssigned={getTimeAssigned(resource.resourceType, resource.storyIds?.length ?? 1)}
                  unassignFromPlan={isNewResource(resource.resourceUniqueId) ? () => removeAssignmentHandler(resource) : () => removeCurrentAssignmentHandler(resource)}
                  isRemoving={false}
                  isNew={isNewResource(resource.resourceUniqueId)}
                  url={resource.url}
                  sharedSkills={resource.sharedSkills}
                />
              ))}

              <div className="assignment-modal-assigned-content-new-header-title">
                <span>Tutoring</span>
                <span style={{ marginLeft: 'auto' }}>{categorizedTutoring.length}</span>
              </div>
              {categorizedTutoring.map((resource: AmiraResources) => (
                <ActivityCard
                  key={`${resource.resourceUniqueId}-current-tutoring`}
                  activityId={resource.resourceUniqueId}
                  title={resource.title}
                  subTitle={resource.resourceType}
                  iconSrc={ResourceTypeImages[resource.resourceType as keyof typeof ResourceTypeImages] || resource.resourceIcon || ""}
                  timeAssigned={getTimeAssigned(resource.resourceType, resource.storyIds?.length ?? 1)}
                  unassignFromPlan={isNewResource(resource.resourceUniqueId) ? () => removeAssignmentHandler(resource) : () => removeCurrentAssignmentHandler(resource)}
                  isRemoving={false}
                  isNew={isNewResource(resource.resourceUniqueId)}
                  url={resource.url}
                  sharedSkills={resource.sharedSkills}
                />
              ))}
              <div className="assignment-modal-assigned-content-new-header-title">
                <span>To be Removed</span>
                <span style={{ marginLeft: 'auto' }}>{removingCurrentAssignments.length}</span>
              </div>
              {removingCurrentAssignments.map((resource: AmiraResources) => (
                <ActivityCard
                  key={`${resource.resourceUniqueId}-current-removing`}
                  activityId={resource.resourceUniqueId}
                  title={resource.title}
                  subTitle={resource.resourceType}
                  iconSrc={ResourceTypeImages[resource.resourceType as keyof typeof ResourceTypeImages] || resource.resourceIcon || ""}
                  timeAssigned={getTimeAssigned(resource.resourceType, resource.storyIds?.length ?? 1)}
                  addToPlan={() => undoRemoveCurrentAssignment(resource)}
                  isRemoving={true}
                  url={resource.url}
                  sharedSkills={resource.sharedSkills}
                />
              ))}
          </div>
      </div>
    </div>
    <div className="assignment-modal-footer d-flex justify-content-between">
      <ButtonThemed
          variant="dark"
          onClick={onCloseHandler}
          disabled={false}
          className="button-with-icon"
        >
          <div className="d-flex align-items-center gap-2">
            <span>
              Cancel
            </span>
          </div>
        </ButtonThemed>
        <ButtonThemed
          variant="dark"
          onClick={batchAssignUnassignStoriesHandler}
          disabled={
            (newAssignments.length === 0 && removingCurrentAssignments.length === 0) ||
            mutateAssignmentsLoading === AsyncLoadingState.LOADING
          }
          className="button-with-icon"
        >
          <div className="d-flex align-items-center gap-2">
            <img src={addToPlanIcon} alt="add to plan icon" className="assignment-modal-submit-button-icon"/>
            {mutateAssignmentsLoading === AsyncLoadingState.LOADING ? 'Saving...' : 'Save Changes'}
          </div>
        </ButtonThemed>
      </div>
    </div>
    <div className="assignment-modal__stories">
      <div className="assignment-modal__stories-header">
      <div className="assignment-modal__stories-title">
      <img src={greenStars} alt="green stars" className="assignment-modal__stories-title-icon"/>
        {searchHeader}
      </div>
      </div>
      <form className="assignment-search-bar">
        <div className="assignment-search-section">
          <div className="assignment-search-input-container">
            <div className="search-text-flow">
              Show me {" "}
              <CustomDropdown
                options={assignmentTypeOptions}
                selectedValue={inputState.assignmentsType}
                onChange={(value: string) => {
                    handleFilterChange('assignmentsType', value);
                }}
                allowMultiple={false}
              />
              from{" "}
              <CustomDropdown
                options={providerOptions}
                selectedValue={inputState.providers}
                onChange={(value: string) => {
                   handleFilterChange('providers', value);
                }}
                allowMultiple={false}
              />
             {" "}for skill{" "}
              <span
                title={skills}
                className="assignment-search-skills-container"
              >
                {skills}
              </span>
            </div>
          </div>
          <div className="assignment-search-icon-container">
            <img className="assignment-search-icon" src={magnifyingGlassIcon} alt="magnifying glass" />
          </div>
        </div>
      </form>
      <div className={`assignment-results-section ${isFiltering ? 'filtering' : ''}`}>
      <div className="assignment-results-title">
        Results ({filteredResources.length})
      </div>
        {isFiltering ? (
          <>
            <Placeholder colWidth={12} heightInRem={8} animation={"wave"} />
            <Placeholder colWidth={12} heightInRem={8} animation={"wave"} />
            <Placeholder colWidth={12} heightInRem={8} animation={"wave"} />
          </>
        ) : (
          <>
            {filteredResources.map((resource: AmiraResources) => (
              <ActivityCard
                key={`${resource.resourceUniqueId}-result`}
                activityId={resource.resourceUniqueId}
                title={resource.title}
                subTitle={resource.resourceType}
                iconSrc={
                  ResourceTypeImages[resource.resourceType as keyof typeof ResourceTypeImages] || resource.resourceIcon || ""
                }
                addToPlan={() => addAssignmentsHandler(resource)}
                isAdded={isActivityAdded(resource.resourceUniqueId)}
                timeAssigned={getTimeAssigned(resource.resourceType, resource.storyIds?.length ?? 1)}
                url={resource.url}
                sharedSkills={resource.sharedSkills}
                isResourceAssigned={false}
              />
            ))}
            {filteredResources.length === 0 && skillResources && skillResources.length > 0 && (
              <div className="no-results-message">
                No resources available for the selected options
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </div>
  </div>
  );
}
